import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  updateRegistrantAccountUserAccountsContact,
  updateRegistrantAccountUserAdmin,
  updateRegistrantAccountUserCanMakePayments,
} from "@/hera-api/client";
import type { RegistrantAccountUserResponseSchema } from "@/hera-api/client/types.gen";
import { invalidateRegistrantUserQueries } from "@/lib/registrant-user-query-invalidation";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/** True when this member receives invoice email, before or after first login. */
export function isAccountsContact(
  row: RegistrantAccountUserResponseSchema,
): boolean {
  return (
    (row.login_identities ?? []).some((li) => li.is_accounts_contact) ||
    Boolean(row.accounts_contact_before_login)
  );
}

/** The org owner. Pending invitees also have a null primary_id until they accept. */
export function isOrgPrimaryUser(
  row: RegistrantAccountUserResponseSchema,
): boolean {
  return !row.primary_id && row.status !== "pending";
}

/**
 * Roles held by a member, in a fixed order so rows stay scannable: the
 * settable ones first, then Primary and Billing, which are changed through
 * their own confirmation dialogs rather than a toggle.
 */
export function getUserRoles(
  row: RegistrantAccountUserResponseSchema,
  paymentAccountId?: string,
): string[] {
  const roles: string[] = [];
  const isPrimary = isOrgPrimaryUser(row);
  // The primary is always an admin; the database enforces it.
  if (row.is_admin || isPrimary) {
    roles.push("Admin");
  }
  if (isAccountsContact(row)) {
    roles.push("Accounts Contact");
  }
  if (row.can_make_payments) {
    roles.push("Can pay");
  }
  if (isPrimary) {
    roles.push("Primary");
  }
  if (paymentAccountId && paymentAccountId === row.id) {
    roles.push("Billing");
  }
  return roles;
}

/** Hover text for each role, so the short labels do not have to carry the meaning. */
const ROLE_DESCRIPTIONS: Record<string, string> = {
  Admin:
    "Can manage organization users: invite, disable, and grant these roles. The primary is always an admin.",
  "Accounts Contact":
    "Receives invoice and billing email for the organization. More than one person can hold this.",
  "Can pay":
    "Can use Pay Now and open Stripe billing. Registering, renewing, and Pay Later work without it.",
  Primary:
    "Owns the organization; every other member is linked to this account. Change it with Make Primary.",
  Billing:
    "The single Stripe customer used for card and ACH checkout. Change it with Set as Billing Account.",
};

interface UserRolesCellProps {
  row: RegistrantAccountUserResponseSchema;
  paymentAccountId?: string;
  editable?: boolean;
  onRowChange?: (row: RegistrantAccountUserResponseSchema) => void;
}

/**
 * Invoice email is an account-level intent: every portal login on the account
 * carries the same flag, and members with no login yet hold it pre-login.
 */
function withAccountsContact(
  row: RegistrantAccountUserResponseSchema,
  checked: boolean,
): RegistrantAccountUserResponseSchema {
  const loginIdentities = row.login_identities ?? [];
  return {
    ...row,
    accounts_contact_before_login:
      loginIdentities.length === 0
        ? checked
        : row.accounts_contact_before_login,
    login_identities: loginIdentities.map((identity) => ({
      ...identity,
      is_accounts_contact: checked,
    })),
  };
}

export function UserRolesCell({
  row,
  paymentAccountId,
  editable = false,
  onRowChange,
}: UserRolesCellProps) {
  const queryClient = useQueryClient();
  const isActive = row.status === "active";
  const isPending = row.status === "pending";
  const isPrimary = isOrgPrimaryUser(row);
  const adminOn = Boolean(row.is_admin) || isPrimary;
  const accountsContactOn = isAccountsContact(row);
  const canPayOn = Boolean(row.can_make_payments);
  const deferPersist = Boolean(onRowChange);

  const mutation = useMutation({
    mutationFn: async ({
      role,
      checked,
    }: {
      role: "admin" | "accountsContact" | "canPay";
      checked: boolean;
    }) => {
      if (role === "admin") {
        const response = await updateRegistrantAccountUserAdmin({
          path: { user_id: row.id },
          body: { is_admin: checked },
        });
        if (response.error) throw response.error;
        return;
      }
      if (role === "canPay") {
        const response = await updateRegistrantAccountUserCanMakePayments({
          path: { user_id: row.id },
          body: { can_make_payments: checked },
        });
        if (response.error) throw response.error;
        return;
      }

      const response = await updateRegistrantAccountUserAccountsContact({
        path: { user_id: row.id },
        body: { is_accounts_contact: checked },
      });
      if (response.error) throw response.error;
    },
    onSuccess: () => invalidateRegistrantUserQueries(queryClient),
    onError: (error) => {
      toast({
        title: "Error",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const toggleRole = (
    role: "admin" | "accountsContact" | "canPay",
    checked: boolean,
  ) => {
    if (onRowChange) {
      if (role === "admin") {
        onRowChange({ ...row, is_admin: checked });
      } else if (role === "canPay") {
        onRowChange({ ...row, can_make_payments: checked });
      } else {
        onRowChange(withAccountsContact(row, checked));
      }
      return;
    }
    mutation.mutate({ role, checked });
  };

  const toggle = (
    label: string,
    checked: boolean,
    role: "admin" | "accountsContact" | "canPay",
    enabled: boolean,
  ) =>
    enabled ? (
      <Button
        key={label}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={`${label} ${row.email}`}
        title={ROLE_DESCRIPTIONS[label]}
        variant={checked ? "default" : "outline"}
        size="xs"
        className="h-auto py-0.5"
        disabled={!deferPersist && mutation.isPending}
        onClick={(event) => {
          event.stopPropagation();
          toggleRole(role, !checked);
        }}
      >
        {label}
      </Button>
    ) : checked ? (
      <Badge key={label} variant="default" title={ROLE_DESCRIPTIONS[label]}>
        {label}
      </Badge>
    ) : null;

  const roles = [
    toggle(
      "Admin",
      adminOn,
      "admin",
      editable && (isActive || isPending) && !isPrimary,
    ),
    toggle(
      "Accounts Contact",
      accountsContactOn,
      "accountsContact",
      editable && (isActive || isPending),
    ),
    toggle("Can pay", canPayOn, "canPay", editable && (isActive || isPending)),
    isPrimary ? (
      <Badge key="Primary" variant="default" title={ROLE_DESCRIPTIONS.Primary}>
        Primary
      </Badge>
    ) : null,
    paymentAccountId === row.id ? (
      <Badge key="Billing" variant="default" title={ROLE_DESCRIPTIONS.Billing}>
        Billing
      </Badge>
    ) : null,
  ].filter(Boolean);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {roles.length > 0 ? (
        roles
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}
